import type { DadosEmpresa } from '@/features/configuracoes/api/configuracoesApi';
import type { VendaDetalhe } from '../api/pdvApi';

type Props = {
  venda: VendaDetalhe;
  nomeEmpresa: string;
  empresa?: DadosEmpresa | null;
  termo?: string | null;
};

const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const dataHoraFmt = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatarDesconto(tipo: string | null, valor: number | null): string | null {
  if (!tipo || valor == null || valor === 0) return null;
  return tipo === 'Percentual' ? `${valor}%` : moeda.format(valor);
}

/**
 * Cupom da venda de balcão, no mesmo papel de 58mm da OS — reusa a folha
 * de estilo `cupom-58` e a página `@page cupom-termico-58` que já
 * existem, então não há layout novo a manter.
 */
export function CupomVenda58mm({ venda, nomeEmpresa, empresa, termo }: Props) {
  const nome = empresa?.nomeEmpresa?.trim() || nomeEmpresa;
  const linhaEndereco = [empresa?.endereco, empresa?.numero, empresa?.bairro]
    .filter(Boolean)
    .join(', ');
  const termoLimpo = (() => {
    const limpo = termo?.replace(/<p>\s*<\/p>/g, '').trim() ?? '';
    return limpo.length > 0 ? limpo : null;
  })();
  const trocoTotal = venda.pagamentos.reduce((acc, p) => acc + (p.troco ?? 0), 0);

  return (
    <div className="cupom-58">
      <div className="cupom-58__empresa">{nome}</div>
      {linhaEndereco && <div className="cupom-58__bloco">{linhaEndereco}</div>}
      {empresa?.telefone && <div className="cupom-58__bloco">{empresa.telefone}</div>}

      <Divisor />

      <div className="cupom-58__empresa">VENDA {venda.numero}</div>
      <div className="cupom-58__bloco">{dataHoraFmt.format(new Date(venda.data))}</div>
      <Linha rotulo="Cliente" valor={venda.clienteNome} />
      {venda.colaboradorNome && <Linha rotulo="Operador" valor={venda.colaboradorNome} />}

      <Divisor />

      {venda.itens.map((i) => (
        <div key={i.id} className="cupom-58__item">
          <div className="cupom-58__item-desc">{i.descricao}</div>
          <div className="cupom-58__linha">
            <span>
              {i.quantidade} x {moeda.format(i.valorUnitario)}
            </span>
            <span>{moeda.format(i.subtotal)}</span>
          </div>
          {formatarDesconto(i.descontoTipo, i.descontoValor) && (
            <div className="cupom-58__linha cupom-58__linha--muted">
              <span>Desc.</span>
              <span>{formatarDesconto(i.descontoTipo, i.descontoValor)}</span>
            </div>
          )}
        </div>
      ))}

      <Divisor />

      {venda.valorDesconto > 0 && (
        <div className="cupom-58__linha cupom-58__linha--muted">
          <span>Subtotal</span>
          <span>{moeda.format(venda.valorBruto)}</span>
        </div>
      )}
      {venda.valorDesconto > 0 && (
        <div className="cupom-58__linha cupom-58__linha--muted">
          <span>Descontos</span>
          <span>- {moeda.format(venda.valorDesconto)}</span>
        </div>
      )}
      <div className="cupom-58__linha cupom-58__total">
        <span>TOTAL</span>
        <span>{moeda.format(venda.valorTotal)}</span>
      </div>

      <Divisor />

      {venda.pagamentos.map((p) => (
        <div key={p.id}>
          <div className="cupom-58__linha">
            <span>{p.formaPagamento}</span>
            <span>{moeda.format(p.valor)}</span>
          </div>
          {p.valorRecebido != null && (
            <div className="cupom-58__linha cupom-58__linha--muted">
              <span>Recebido</span>
              <span>{moeda.format(p.valorRecebido)}</span>
            </div>
          )}
        </div>
      ))}
      {trocoTotal > 0 && (
        <div className="cupom-58__linha cupom-58__total">
          <span>TROCO</span>
          <span>{moeda.format(trocoTotal)}</span>
        </div>
      )}

      {venda.observacoes && (
        <>
          <Divisor />
          <div className="cupom-58__bloco">{venda.observacoes}</div>
        </>
      )}

      {termoLimpo && (
        <>
          <Divisor />
          <div
            className="cupom-58__termo"
            dangerouslySetInnerHTML={{ __html: termoLimpo }}
          />
        </>
      )}

      <Divisor />
      <div className="cupom-58__bloco cupom-58__linha--muted">
        Este documento não tem valor fiscal.
      </div>
    </div>
  );
}

function Divisor() {
  return <div className="cupom-58__divisor">--------------------------------</div>;
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="cupom-58__bloco">
      <span className="cupom-58__rotulo">{rotulo}: </span>
      <span>{valor}</span>
    </div>
  );
}
